<!-- Content transcribed from the supplied 简版 V2 prototype. Source-block IDs retain its original manuscript mapping. -->
<template>
  <div>
    <section class="view article-view" v-show="topic === 'start'">
      <div class="article-heading">
        <h1 tabindex="-1">第一次使用</h1>
      </div>
      <p class="lead">按下面的顺序，完成第一次使用。</p>
      <p class="notice">请使用随机原装电源适配器和数据线。安装、清洁与更换芳香耗材前，请先断开电源。</p>
      <section class="step">
        <h2><span>1</span>摆放设备</h2>
        <p>将主机放在床头平稳、干燥的地方。屏幕面正对人体胸腹部，距离 0.3–1.5 米，中间不要有大件遮挡。</p>
        <div class="distance">
          <span>设备</span>
          <i></i>
          <strong>0.3–1.5 米</strong>
          <i></i>
          <span>胸腹部</span>
        </div>
      </section>
      <section class="step">
        <h2><span>2</span>装入芳香瓶与棉棒</h2>
        <p>向上打开顶部盖子，将专用芳香瓶垂直放入芳香槽。把棉棒插入雾化导管到底，再将导管垂直插入瓶中，盖好上盖。</p>
        <div class="paired-images">
          <sleep-manual-image asset="image2" caption="芳香瓶放入芳香槽" @preview="$emit('preview', $event)" />
          <sleep-manual-image asset="image3" caption="棉棒与雾化导管安装" @preview="$emit('preview', $event)" />
        </div>
      </section>
      <section class="step">
        <h2><span>3</span>接通电源并开机</h2>
        <p>连接随机原装电源适配器和 Type-C 数据线。长按机身背部电源键 3 秒，屏幕亮起后等待启动，启动约需 30 秒。</p>
        <sleep-manual-image asset="image4" caption="机身背部的电源键位置" @preview="$emit('preview', $event)" />
      </section>
      <section class="step">
        <h2><span>4</span>联网并绑定手机 App</h2>
        <p>在设备屏幕上选择家庭 Wi-Fi 并完成连接。打开“八戒健康 App”，选择“睡眠管家服务”，用手机号注册。依次点击“开始准备”“扫码连接”，扫描本机屏幕二维码。</p>
        <div class="paired-images">
          <sleep-manual-image asset="image6" caption="选择 Wi-Fi 并输入密码" @preview="$emit('preview', $event)" />
          <sleep-manual-image asset="image8" caption="八戒健康 App 扫描设备屏幕二维码，图中码已遮挡" @preview="$emit('preview', $event)" />
        </div>
        <p class="caption-note">请扫描你自己设备屏幕上的二维码，说明图中的码不可用于绑定。</p>
      </section>
      <section class="step">
        <h2><span>5</span>开始今晚的睡眠</h2>
        <p>进入设备睡眠首页，按需要调整今晚方案，点击“开始睡眠”。第二天醒来后，点击“结束睡眠”，完成本晚记录。</p>
        <sleep-manual-image asset="image13" caption="机载 App 开始睡眠入口" @preview="$emit('preview', $event)" />
      </section>
      <router-link class="inline-link" :to="manualLink('use')">继续查看日常使用<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></router-link>
      <router-link class="return-home" :to="manualLink('home')">返回说明首页</router-link>
    </section>
    <section class="view article-view" v-show="topic === 'use'">
      <div class="article-heading">
        <h1 tabindex="-1">日常使用</h1>
      </div>
      <p class="lead">按你想了解的操作，点开查看。</p>
      <details class="fold" open>
        <summary>开始与结束睡眠<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></summary>
        <div class="fold-body">
          <p>在设备首页点击“开始睡眠”。第二天醒来后点击“结束睡眠”，并确认结束，完成一晚睡眠记录。</p>
          <sleep-manual-image asset="image17" caption="结束睡眠的确认提示" @preview="$emit('preview', $event)" />
        </div>
      </details>
      <details class="fold">
        <summary>调整今晚方案<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></summary>
        <div class="fold-body">
          <p>在“调整方案”中，可开启或关闭香薰、音乐、灯光与舒曼波，并调整音量大小。设置以设备实际显示为准。</p>
          <sleep-manual-image asset="image14" caption="调整今晚方案与统一音量" @preview="$emit('preview', $event)" />
        </div>
      </details>
      <details class="fold">
        <summary>跟随音频做呼吸练习<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></summary>
        <div class="fold-body">
          <p>打开呼吸练习，放松身体，自然跟随音频引导的节奏。</p>
          <sleep-manual-image asset="image15" caption="呼吸练习音频引导" @preview="$emit('preview', $event)" />
        </div>
      </details>
      <details class="fold">
        <summary>用语音控制设备<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></summary>
        <div class="fold-body">
          <p>先说“小嫦小嫦”或“你好小嫦”，再说出指令。</p>
          <ul>
            <li>“我要睡觉了”</li>
            <li>“打开睡眠模式”</li>
            <li>“关闭灯光”</li>
            <li>“打开香薰”</li>
            <li>“打开舒曼波”</li>
            <li>“调大音量”或“调小音量”</li>
          </ul>
          <p>语音指令的完整说明见原说明书第 4 章。</p>
        </div>
      </details>
      <details class="fold">
        <summary>查看设备信息与更新<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></summary>
        <div class="fold-body">
          <p>进入设备详情查看设备信息。机载 App 的更新请按官方页面提示操作，勿点击来源不明的更新链接。硬件固件远程升级不在原稿所述 V2.0 默认范围内。</p>
          <sleep-manual-image asset="image18" caption="查看设备信息，序列号已遮挡" @preview="$emit('preview', $event)" />
        </div>
      </details>
      <router-link class="return-home" :to="manualLink('home')">返回说明首页</router-link>
    </section>
    <section class="view article-view" v-show="topic === 'faq'">
      <div class="article-heading">
        <h1 tabindex="-1">常见问题</h1>
      </div>
      <p class="lead">找到你遇到的情况，点开查看处理方法。</p>
      <details class="fold">
        <summary>芳香液中有少量絮状物，正常吗？<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></summary>
        <div class="fold-body">
          <p>芳香液中如有少量絮状物，是植物成份自然析出的表现，属于正常现象。</p>
        </div>
      </details>
      <details class="fold">
        <summary>雷达监测不准确<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></summary>
        <div class="fold-body">
          <p>检查主机摆放位置，确保雷达面正对人体胸腹部、无遮挡，距离 0.3 m – 1.5 m。</p>
        </div>
      </details>
      <details class="fold">
        <summary>Wi-Fi 连接失败<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></summary>
        <div class="fold-body">
          <p>确认密码正确、信号稳定；切换到 2.4 GHz；必要时重启路由器与主机。</p>
        </div>
      </details>
      <details class="fold">
        <summary>雾化效果异常<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></summary>
        <div class="fold-body">
          <p>可能是雾化片干磨，请检查精油瓶是否安装到位、精油是否充足。</p>
        </div>
      </details>
      <details class="fold">
        <summary>呼吸引导无法同步<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></summary>
        <div class="fold-body">
          <p>放松身体自然跟随引导信号节奏；如音频异常请检查扬声器出声孔是否堵塞。</p>
        </div>
      </details>
      <details class="fold">
        <summary>App 不响应<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></summary>
        <div class="fold-body">
          <p>按电源键 10 秒强制重启；更新至最新版本；如仍异常请联系售后。</p>
        </div>
      </details>
      <details class="fold">
        <summary>终端长时间不上传<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></summary>
        <div class="fold-body">
          <p>检查网络、电源；App 内查看&quot;上次连接时间&quot;；可手动进入睡眠模式触发上报。</p>
        </div>
      </details>
      <details class="fold">
        <summary>控制未生效<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></summary>
        <div class="fold-body">
          <p>在 App 内查看&quot;结果待确认&quot;状态；如长期未生效，请重启主机或联系售后。</p>
        </div>
      </details>
      <details class="fold">
        <summary>香薰频次调整失败<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></summary>
        <div class="fold-body">
          <p>调整范围与步长以硬件能力为准；请按页面提示的合法范围操作。</p>
        </div>
      </details>
      <details class="fold">
        <summary>设备运行异响<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></summary>
        <div class="fold-body">
          <p>检查精油瓶是否安装到位、精油是否充足；如无法消除请停止使用并联系售后。</p>
        </div>
      </details>
      <div class="help-line">
        问题仍未解决？
        <router-link :to="manualLink('service')">查看售后方式</router-link>
      </div>
      <router-link class="return-home" :to="manualLink('home')">返回说明首页</router-link>
    </section>
    <section class="view article-view" v-show="topic === 'safety'">
      <div class="article-heading">
        <h1 tabindex="-1">安全与清洁</h1>
      </div>
      <p class="notice">出现破损、过热、异味、冒烟、漏液或进水时，请停止使用、切断电源并联系售后。非原厂授权人员请勿拆机。</p>
      <details class="fold" open>
        <summary>使用前先看这几条<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></summary>
        <div class="fold-body">
          <ul>
            <li>使用随机原装适配器和数据线，禁止湿手插拔电源。</li>
            <li>保持设备干燥，远离水源、火源、热源；不要覆盖机身或堵塞散热孔。</li>
            <li>请将主机、配件和芳香瓶放在儿童无法触及的位置。</li>
            <li>植入电子医疗器械者、孕妇、婴幼儿及对芳香成分过敏等人群，使用前请咨询医生。</li>
            <li>清洁维护前先断电，精油瓶漏液时立即拔下电源。</li>
          </ul>
        </div>
      </details>
      <details class="fold">
        <summary>清洁与保养<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></summary>
        <div class="fold-body">
          <div class="source-block" data-source-block="138">
            <p class="source-line">清洁前请先断开电源。</p>
          </div>
          <div class="source-block" data-source-block="139">
            <p class="source-line">使用柔软干布擦拭机身外壳；禁止使用腐蚀性或研磨性清洁剂（如酒精、稀释剂、抛光膏等）。</p>
          </div>
          <div class="source-block" data-source-block="140">
            <p class="source-line">禁止将设备浸入水中或用水直接冲洗。</p>
          </div>
          <div class="source-block" data-source-block="141">
            <p class="source-line">精油槽内部清洁请使用棉签蘸取少量医用酒精轻拭；清洁后请充分晾干再装入精油瓶。</p>
          </div>
          <div class="source-block" data-source-block="142">
            <p class="source-line">扬声器出声孔、雷达出光面请使用干燥、不掉毛的软毛刷轻拭，避免刮花表面。</p>
          </div>
        </div>
      </details>
      <details class="fold">
        <summary>芳香耗材与高温提示<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></summary>
        <div class="fold-body">
          <p>禁止加热模组在精油瓶缺失、空瓶或破损状态下使用。</p>
          <p>开盖后精油加热棒温度较高（可达约 60 ℃），在断电 30 分钟内勿用手直接触碰加热棒金属部分。</p>
          <p>精油瓶为易碎玻璃，禁止跌落、挤压；如出现漏液请立即拔下电源并用干布擦拭，避免腐蚀底盘。</p>
          <p>对精油成分过敏者、哮喘患者、孕妇、婴幼儿及免疫低下人群，使用前请咨询医生，并选择无刺激配方。</p>
          <p>请将本机、适配器、配件放置于儿童无法触及的位置；精油瓶需放置在儿童无法打开、无法打翻的位置。</p>
        </div>
      </details>
      <details class="fold">
        <summary>摆放与使用环境<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></summary>
        <div class="fold-body">
          <p>工作温度 0 ℃ – 40 ℃，湿度 10 % – 90 % RH（无凝露）。</p>
          <p>避免在高温、高湿、粉尘或强电磁干扰环境中使用。</p>
          <p>避免在气压急剧变化（如登机前后）时使用雷达采集功能。</p>
          <p>请使用稳定的 5 V / 2 A 适配器与标准交流 220 V 电网；山区、农村或老旧建筑电网不稳时，请加装稳压装置。</p>
          <p>将主机放置于床头平稳干燥处，雷达面正对人体且无大面积遮挡。</p>
          <p>禁止与其它高功率电器（微波炉、电热水器等）共用同一插座。</p>
          <p>电源线应避开尖锐边角、活动家具、踏脚位置。</p>
          <p>设备长时间不使用时，请从插座上拔下插头断开电源。</p>
        </div>
      </details>
      <router-link class="inline-link" :to="manualLink('manual')">查看完整安全与无线电说明<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></router-link>
      <p class="health-note">本产品为智能健康电子产品，不属于医疗器械。各项指标及风险提醒仅供健康参考，不替代专业医疗诊断。</p>
      <router-link class="return-home" :to="manualLink('home')">返回说明首页</router-link>
    </section>
    <section class="view article-view" v-show="topic === 'service'">
      <div class="article-heading">
        <h1 tabindex="-1">参数与售后</h1>
      </div>
      <details class="fold" open>
        <summary>主要参数<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></summary>
        <div class="fold-body">
          <div class="table-wrap">
            <table>
              <tbody>
                <tr>
                  <th scope="row">产品名称</th>
                  <td>睡眠管家</td>
                </tr>
                <tr>
                  <th scope="row">雷达有效检测距离</th>
                  <td>0.3 m – 1.5 m</td>
                </tr>
                <tr>
                  <th scope="row">电源输入（适配器）</th>
                  <td>DC 5 V / 2 A</td>
                </tr>
                <tr>
                  <th scope="row">整机功耗（满载）</th>
                  <td>≤ 8 W</td>
                </tr>
                <tr>
                  <th scope="row">整机尺寸（约）</th>
                  <td>120 × 120 × 180 mm</td>
                </tr>
                <tr>
                  <th scope="row">整机重量（约）</th>
                  <td>650 g</td>
                </tr>
                <tr>
                  <th scope="row">工作温度</th>
                  <td>0 ℃ – 40 ℃</td>
                </tr>
                <tr>
                  <th scope="row">工作湿度</th>
                  <td>10 % – 90 % RH（无凝露）</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </details>
      <details class="fold">
        <summary>包装清单<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></summary>
        <div class="fold-body">
          <div class="table-wrap">
            <table>
              <tbody>
                <tr>
                  <th scope="row">睡眠管家主机</th>
                  <td>1 台</td>
                </tr>
                <tr>
                  <th scope="row">Type-C 数据线</th>
                  <td>1 根</td>
                </tr>
                <tr>
                  <th scope="row">电源适配器（DC 5 V / 2 A）</th>
                  <td>1 个</td>
                </tr>
                <tr>
                  <th scope="row">使用说明书（含保修卡、合格证）</th>
                  <td>1 份</td>
                </tr>
                <tr>
                  <th scope="row">芳香液</th>
                  <td>3盒</td>
                </tr>
                <tr>
                  <th scope="row">棉棒</th>
                  <td>1根</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </details>
      <details class="fold">
        <summary>保修范围与期限<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></summary>
        <div class="fold-body">
          <div class="table-wrap">
            <table>
              <tbody>
                <tr>
                  <th scope="row">主机及雷达模块</th>
                  <td>自购买之日起 12 个月</td>
                </tr>
                <tr>
                  <th scope="row">适配器及 Type-C 数据线</th>
                  <td>自购买之日起 6 个月</td>
                </tr>
                <tr>
                  <th scope="row">精油瓶等耗材</th>
                  <td>不在保修范围内</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p>下列情况不在保修范围内：人为损坏、进水、使用非原厂耗材、自行拆卸、私自改装及不可抗力造成的损坏。</p>
          <p>为享受保修服务，请保留完整购买凭证、保修卡与原包装；如需维修请随附故障描述与联系方式。</p>
        </div>
      </details>
      <section class="service-contact">
        <h2>联系售后</h2>
        <dl>
          <dt>客服热线</dt>
          <dd>详见机内保修卡</dd>
          <dt>微信公众号</dt>
          <dd>八戒睡眠</dd>
          <dt>售后地址</dt>
          <dd>浙江省杭州市西湖区文三西路478号<br />华星时代广场A座1308</dd>
        </dl>
        <p>请准备购买凭证、保修卡、设备信息和故障描述。</p>
      </section>
      <router-link class="return-home" :to="manualLink('home')">返回说明首页</router-link>
    </section>
    <section class="view article-view" v-show="topic === 'manual'">
      <div class="article-heading">
        <h1 tabindex="-1">完整说明书</h1>
      </div>
      <p class="lead">以下保留原说明书的章节与内容，并纳入后续补充，按需展开阅读。</p>
      <p class="draft-note">原稿保留及补充版。说明书版本、报告生成时间及部分认证表述仍需在正式发布前核对。</p>
      <details class="fold">
        <summary>前言与适用范围<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></summary>
        <div class="fold-body">
          <div class="source-block" data-source-block="0">
            <h3>睡眠管家  使用说明书</h3>
          </div>
          <div class="source-block" data-source-block="1">
            <p>产品名称：睡眠管家</p>
          </div>
          <div class="source-block" data-source-block="2">
            <p>执行标准：GB 4943.1-2022《音视频、信息技术和通信技术设备 第1部分：安全要求》</p>
          </div>
          <div class="source-block" data-source-block="3">
            <p>请在使用本产品前仔细阅读本说明书，并妥善保管。</p>
          </div>
          <div class="source-block" data-source-block="4">
            <p>制造商：杭州八戒睡眠医学技术有限公司</p>
          </div>
          <div class="source-block" data-source-block="5">
            <p>地址：中国（浙江）自由贸易试验区杭州市滨江区长河街道江二路57号2幢D区302室</p>
          </div>
          <div class="source-block" data-source-block="6">
            <p>客服热线、官方微信公众号：详见机内保修卡</p>
          </div>
          <div class="source-block" data-source-block="7">
            <p>无线电发射设备型号核准代码（CMIIT ID）：26J333WV5273</p>
          </div>
          <div class="source-block" data-source-block="8">
            <h3>前言</h3>
          </div>
          <div class="source-block" data-source-block="9">
            <p>本说明书依据 GB 4943.1-2022《音视频、信息技术和通信技术设备 第1部分：安全要求》的要求编写，并结合产品实际功能（包括机载 App、云端通信架构、60 GHz 毫米波雷达、香薰雾化模组、舒曼波及五维闭环睡眠干预体系）编写。说明书详细描述了产品的安装、操作、维护、故障处理、安全注意事项、有害物质声明与报废回收等内容，是用户正确、安全使用本产品的重要依据。</p>
          </div>
          <div class="source-block" data-source-block="10">
            <p>本说明书适用于睡眠管家及其随机配送附件。请将本说明书与保修卡、合格证一并妥善保管，以备日后查阅。产品升级、固件更新或法定标准变更时，制造商可能更新本说明书内容，请关注官方微信公众号。</p>
          </div>
          <div class="source-block" data-source-block="11">
            <p>本说明书中的图示仅供参考，实际界面以机载 App实际显示为准。</p>
          </div>
          <div class="source-block" data-source-block="12">
            <p>本说明书主要内容如下：</p>
          </div>
          <div class="source-block" data-source-block="13">
            <p class="source-line">第 1 章 产品简介：产品定位、原理、规格参数、包装清单。</p>
          </div>
          <div class="source-block" data-source-block="14">
            <p class="source-line">第 2 章 硬件的安装与使用：硬件安装、上电、启动。</p>
          </div>
          <div class="source-block" data-source-block="15">
            <p class="source-line">第 3 章 机载 App使用流程：联网、绑定、首页、调整方案、呼吸练习、睡眠进行、睡眠结束、设备详情。</p>
          </div>
          <div class="source-block" data-source-block="16">
            <p class="source-line">第 4 章 语音交互。</p>
          </div>
          <div class="source-block" data-source-block="17">
            <p class="source-line">第 5 章 机载 App 远程更新。</p>
          </div>
          <div class="source-block" data-source-block="18">
            <p class="source-line">第 6 章 安全说明与注意事项：电击、火灾、机械、热灼伤、光学辐射及微波辐射防护。</p>
          </div>
          <div class="source-block" data-source-block="19">
            <p class="source-line">第 7 章 清洁与维护、芳香耗材管理。</p>
          </div>
          <div class="source-block" data-source-block="20">
            <p class="source-line">第 8 章 常见问题、故障排除、保修与服务。</p>
          </div>
          <div class="source-block" data-source-block="21">
            <p class="source-line">第 9 章 有害物质声明、废弃处理与无线电发射设备声明。</p>
          </div>
          <div class="source-block" data-source-block="22">
            <p class="source-line">第 10 章 技术规格、执行标准、制造商信息。</p>
          </div>
        </div>
      </details>
      <details class="fold">
        <summary>产品简介<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></summary>
        <div class="fold-body">
          <div class="source-block" data-source-block="23">
            <h3>1  产品简介</h3>
          </div>
          <div class="source-block" data-source-block="24">
            <h3>1.1  产品定位</h3>
          </div>
          <div class="source-block" data-source-block="25">
            <p>睡眠管家是一款集成高精度无感监测与多维物理干预的非药物睡眠干预终端。本产品属智能健康电子产品，各项指标及风险提醒仅供健康参考，不替代专业医疗诊断，不具备医疗诊断效力。如有持续失眠、睡眠呼吸暂停或其他疑似疾病，请及时就医。</p>
          </div>
          <div class="source-block" data-source-block="26">
            <h3>1.2  工作原理</h3>
          </div>
          <div class="source-block" data-source-block="27">
            <p>本产品通过&quot;监测—分析—干预—反馈&quot;实现全自动助眠管理：</p>
          </div>
          <div class="source-block" data-source-block="28">
            <p class="source-line">无感监测：内置 60 GHz 毫米波（mmWave）雷达，提取呼吸、心率波形与体动信号，无需接触身体。</p>
          </div>
          <div class="source-block" data-source-block="29">
            <p class="source-line">智能分析：基于轻量级神经网络模型识别睡眠阶段（清醒、浅睡、中睡、深睡），并通过雷达扩展字段记录呼吸暂停次数、最长暂停、平均暂停、HRV 等。</p>
          </div>
          <div class="source-block" data-source-block="30">
            <p class="source-line">多维干预：根据睡眠阶段动态协同输出五种干预手段——芳香疗法、音乐疗法、光照、舒曼波（低频脉冲）、呼吸吐纳闭环引导。</p>
          </div>
          <div class="source-block" data-source-block="31">
            <p class="source-line">睡眠维持与防觉醒：检测到 HRV 低频/高频比上升或呼吸频率增加等觉醒前兆时，自动启动软干预（低音量节律音轨 + 脉冲补释芳香），帮助用户在不完全觉醒状态下重新沉入深睡。</p>
          </div>
          <div class="source-block" data-source-block="32">
            <p class="source-line">成效反馈：次日 结束睡眠后生成睡眠日报，并上传至云端；通过八戒睡眠APP查看睡眠日报。</p>
          </div>
          <div class="source-block" data-source-block="33">
            <h3>1.3  技术规格</h3>
          </div>
          <div class="source-block" data-source-block="34">
            <div class="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th scope="col">项目</th>
                    <th scope="col">参数</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row">产品名称</th>
                    <td>睡眠管家</td>
                  </tr>
                  <tr>
                    <th scope="row">软件版本</th>
                    <td>V2.1</td>
                  </tr>
                  <tr>
                    <th scope="row">雷达工作频率</th>
                    <td>60 GHz</td>
                  </tr>
                  <tr>
                    <th scope="row">雷达有效检测距离</th>
                    <td>0.3 m – 1.5 m</td>
                  </tr>
                  <tr>
                    <th scope="row">电源输入（适配器）</th>
                    <td>DC 5 V / 2 A</td>
                  </tr>
                  <tr>
                    <th scope="row">整机功耗（满载）</th>
                    <td>≤ 8 W</td>
                  </tr>
                  <tr>
                    <th scope="row">待机功耗</th>
                    <td>&lt; 0.5 W</td>
                  </tr>
                  <tr>
                    <th scope="row">无线局域网</th>
                    <td>2.4 / 5.1 / 5.8 GHz 三频；最高发射功率 ≤ 33 dBm（e.i.r.p.）；CMIIT ID：26J333WV5273</td>
                  </tr>
                  <tr>
                    <th scope="row">音频输出</th>
                    <td>内置扬声器（单声道）</td>
                  </tr>
                  <tr>
                    <th scope="row">整机尺寸（约）</th>
                    <td>120 × 120 × 180 mm</td>
                  </tr>
                  <tr>
                    <th scope="row">整机重量（约）</th>
                    <td>650 g</td>
                  </tr>
                  <tr>
                    <th scope="row">工作温度</th>
                    <td>0 ℃ – 40 ℃</td>
                  </tr>
                  <tr>
                    <th scope="row">工作湿度</th>
                    <td>10 % – 90 % RH（无凝露）</td>
                  </tr>
                  <tr>
                    <th scope="row">存储温度</th>
                    <td>−20 ℃ – 60 ℃</td>
                  </tr>
                  <tr>
                    <th scope="row">执行安全标准</th>
                    <td>GB 4943.1-2022《音视频、信息技术和通信技术设备 第1部分：安全要求》</td>
                  </tr>
                  <tr>
                    <th scope="row">微功率无线电</th>
                    <td>微功率短距离无线电设备（60 GHz 雷达）</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="source-block" data-source-block="35">
            <h3>1.4  包装清单</h3>
          </div>
          <div class="source-block" data-source-block="36">
            <p>拆封后请对照下表清点物品；如有缺漏，请联系销售商：</p>
          </div>
          <div class="source-block" data-source-block="37">
            <div class="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th scope="col">序号</th>
                    <th scope="col">名称</th>
                    <th scope="col">数量</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row">1</th>
                    <td>睡眠管家主机</td>
                    <td>1 台</td>
                  </tr>
                  <tr>
                    <th scope="row">2</th>
                    <td>Type-C 数据线</td>
                    <td>1 根</td>
                  </tr>
                  <tr>
                    <th scope="row">3</th>
                    <td>电源适配器（DC 5 V / 2 A）</td>
                    <td>1 个</td>
                  </tr>
                  <tr>
                    <th scope="row">4</th>
                    <td>使用说明书（含保修卡、合格证）</td>
                    <td>1 份</td>
                  </tr>
                  <tr>
                    <th scope="row">5</th>
                    <td>芳香液</td>
                    <td>3盒</td>
                  </tr>
                  <tr>
                    <th scope="row">6</th>
                    <td>棉棒</td>
                    <td>1根</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </details>
      <details class="fold">
        <summary>硬件安装与使用<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></summary>
        <div class="fold-body">
          <div class="source-block" data-source-block="38">
            <h3>2  硬件的安装与使用</h3>
          </div>
          <div class="source-block" data-source-block="39">
            <p>请按以下步骤完成硬件安装：</p>
          </div>
          <div class="source-block" data-source-block="40">
            <div class="source-images multi">
              <sleep-manual-image asset="image1" caption="打开主机顶部盖子" @preview="$emit('preview', $event)" />
              <sleep-manual-image asset="image2" caption="芳香瓶放入芳香槽" @preview="$emit('preview', $event)" />
              <sleep-manual-image asset="image3" caption="棉棒与雾化导管安装" @preview="$emit('preview', $event)" />
              <sleep-manual-image asset="image4" caption="机身背部的电源键位置" @preview="$emit('preview', $event)" />
            </div>
          </div>
          <div class="source-block" data-source-block="41">
            <p class="source-line">将主机放置于床头柜或平稳干燥表面，雷达面（显示屏面）正对人体胸腹部，距离 0.3 m – 1.5 m，确保中间无大件遮挡。</p>
          </div>
          <div class="source-block" data-source-block="42">
            <p class="source-line">向上打开主机顶部盖子，将专用芳香瓶垂直放入芳香槽内。</p>
          </div>
          <div class="source-block" data-source-block="43">
            <p class="source-line">将棉棒插入雾化导管到底，然后将导管垂直插入芳香瓶中，并盖好上盖。</p>
          </div>
          <div class="source-block" data-source-block="44">
            <p class="source-line">使用随机配送的 Type-C 数据线与电源适配器连接电源。</p>
          </div>
          <div class="source-block" data-source-block="45">
            <p class="source-line">长按机身背部电源键 3 秒开机，屏幕亮起即进入启动界面。</p>
          </div>
          <div class="source-block" data-source-block="46">
            <p class="source-line">⚠ 电源警告：本产品仅限使用随机配送的电源适配器（DC 5 V / 2 A）。禁止使用任何非原装适配器、移动电源、笔记本 USB 口以外的电源供电，以免因电压、电流偏差造成电击、起火或损坏。电源线破损或适配器异常发热时，请立即停止使用并联系售后。</p>
          </div>
        </div>
      </details>
      <details class="fold">
        <summary>机载 App 使用流程<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></summary>
        <div class="fold-body">
          <div class="source-block" data-source-block="47">
            <h3>3  机载 App使用流程</h3>
          </div>
          <div class="source-block" data-source-block="48">
            <p>本章介绍机载 App 自启动起的使用流程。</p>
          </div>
          <div class="source-block" data-source-block="49">
            <h3>3.1  开机页面</h3>
          </div>
          <div class="source-block" data-source-block="50">
            <p>启动时间约 30 秒</p>
          </div>
          <div class="source-block" data-source-block="51">
            <div class="source-images ">
              <sleep-manual-image asset="image5" caption="设备启动页面" @preview="$emit('preview', $event)" />
            </div>
          </div>
          <div class="source-block" data-source-block="52">
            <h3>3.2  网络连接</h3>
          </div>
          <div class="source-block" data-source-block="53">
            <div class="source-images ">
              <sleep-manual-image asset="image6" caption="选择 Wi-Fi 并输入密码" @preview="$emit('preview', $event)" />
            </div>
          </div>
          <div class="source-block" data-source-block="54">
            <div class="source-images ">
              <sleep-manual-image asset="image7" caption="Wi-Fi 连接成功" @preview="$emit('preview', $event)" />
            </div>
          </div>
          <div class="source-block" data-source-block="55">
            <p>如Wi-Fi连接失败，请重新连接Wi-Fi。</p>
          </div>
          <div class="source-block" data-source-block="56">
            <h3>3.3  设备绑定</h3>
          </div>
          <div class="source-block" data-source-block="57">
            <p>用八戒健康APP扫码绑定设备</p>
          </div>
          <div class="source-block" data-source-block="58">
            <div class="source-images ">
              <sleep-manual-image asset="image8" caption="八戒健康 App 扫描设备屏幕二维码，图中码已遮挡" @preview="$emit('preview', $event)" />
            </div>
          </div>
          <div class="source-block" data-source-block="59">
            <p>下载“八戒健康APP” --&gt; 选择“睡眠管家服务”--&gt;手机号完成注册--&gt;点击“开始准备”--&gt;点击“扫码连接”--&gt; 完成设备绑定</p>
          </div>
          <div class="source-block" data-source-block="60">
            <div class="source-images multi">
              <sleep-manual-image asset="image9" caption="选择睡眠管家服务" @preview="$emit('preview', $event)" />
              <sleep-manual-image asset="image10" caption="手机号注册" @preview="$emit('preview', $event)" />
              <sleep-manual-image asset="image11" caption="点击开始准备" @preview="$emit('preview', $event)" />
              <sleep-manual-image asset="image12" caption="点击扫码连接" @preview="$emit('preview', $event)" />
            </div>
          </div>
          <div class="source-block" data-source-block="61">
            <p class="source-line">点击&quot;先体验一下&quot;跳过绑定，使用设备。</p>
          </div>
          <div class="source-block" data-source-block="62">
            <h3>3.4  睡眠首页</h3>
          </div>
          <div class="source-block" data-source-block="63">
            <p>点击“开始睡眠”进入睡眠模式</p>
          </div>
          <div class="source-block" data-source-block="64">
            <div class="source-images ">
              <sleep-manual-image asset="image13" caption="机载 App 开始睡眠入口" @preview="$emit('preview', $event)" />
            </div>
          </div>
          <div class="source-block" data-source-block="65">
            <h3>3.5  调整方案</h3>
          </div>
          <div class="source-block" data-source-block="66">
            <p>调整方案中可设置物理干预方式（香薰、音乐、灯光和舒曼波）的开启或关闭，以及音量的大小。</p>
          </div>
          <div class="source-block" data-source-block="67">
            <div class="source-images ">
              <sleep-manual-image asset="image14" caption="调整今晚方案与统一音量" @preview="$emit('preview', $event)" />
            </div>
          </div>
          <div class="source-block" data-source-block="68">
            <h3>3.6  呼吸练习</h3>
          </div>
          <div class="source-block" data-source-block="69">
            <p>呼吸练习中会有音频引导您进行呼吸练习。</p>
          </div>
          <div class="source-block" data-source-block="70">
            <div class="source-images ">
              <sleep-manual-image asset="image15" caption="呼吸练习音频引导" @preview="$emit('preview', $event)" />
            </div>
          </div>
          <div class="source-block" data-source-block="71">
            <h3>3.7  睡眠进行</h3>
          </div>
          <div class="source-block" data-source-block="72">
            <p class="source-line">温湿度展示来自本机温湿度传感器，不与室外天气混用。</p>
          </div>
          <div class="source-block" data-source-block="73">
            <div class="source-images ">
              <sleep-manual-image asset="image16" caption="睡眠进行中与室内温湿度" @preview="$emit('preview', $event)" />
            </div>
          </div>
          <div class="source-block" data-source-block="74">
            <h3>3.8  睡眠结束页面</h3>
          </div>
          <div class="source-block" data-source-block="75">
            <p>第二天醒来，点击睡眠结束，完成一个晚上的睡眠记录。</p>
          </div>
          <div class="source-block" data-source-block="76">
            <div class="source-images ">
              <sleep-manual-image asset="image17" caption="结束睡眠的确认提示" @preview="$emit('preview', $event)" />
            </div>
          </div>
          <div class="source-block" data-source-block="77">
            <h3>3.9  设备详情</h3>
          </div>
          <div class="source-block" data-source-block="78">
            <div class="source-images ">
              <sleep-manual-image asset="image18" caption="查看设备信息，序列号已遮挡" @preview="$emit('preview', $event)" />
            </div>
          </div>
          <div class="source-block" data-source-block="79">
            <p class="source-line">⚠ 雷达信号受检测角度、表面材质、被褥厚度、人体差异影响，实际有效检测距离为 0.3 m – 1.5 m；超距或被金属/水大面积遮挡时，信号可能判为&quot;无效&quot;或&quot;无人&quot;，请按说明书第 6 章&quot;摆放与用电安全&quot;调整主机位置。</p>
          </div>
        </div>
      </details>
      <details class="fold">
        <summary>语音交互<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></summary>
        <div class="fold-body">
          <div class="source-block" data-source-block="80">
            <h3>4  语音交互</h3>
          </div>
          <div class="source-block" data-source-block="81">
            <p>本产品支持离线语音唤醒与控制：</p>
          </div>
          <div class="source-block" data-source-block="82">
            <p class="source-line">唤醒词：&quot;小嫦小嫦&quot;或&quot;你好小嫦&quot;。</p>
          </div>
          <div class="source-block" data-source-block="83">
            <p class="source-line">唤醒后可执行：开关机、待机、报时、调大/调小音量。</p>
          </div>
          <div class="source-block" data-source-block="84">
            <p class="source-line">操作指令格式 = 前缀 + 后缀。例如：</p>
          </div>
          <div class="source-block" data-source-block="85">
            <p class="source-line">  • 前缀：开启、打开、关闭、关掉。</p>
          </div>
          <div class="source-block" data-source-block="86">
            <p class="source-line">  • 后缀：灯光、香薰、舒曼波、音乐、睡眠模式。</p>
          </div>
          <div class="source-block" data-source-block="87">
            <p class="source-line">  • 示例：&quot;打开睡眠模式&quot;、&quot;关闭灯光&quot;、&quot;打开香薰&quot;、&quot;打开舒曼波&quot;。 补充语音示例：“我要睡觉了”。</p>
          </div>
          <div class="source-block" data-source-block="88">
            <p class="source-line">本产品不连接互联网语义服务，所有语音指令在本地完成解析；不收集用户语音原文，仅记录结构化指令结果用于睡眠干预。</p>
          </div>
        </div>
      </details>
      <details class="fold">
        <summary>机载 App 远程更新<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></summary>
        <div class="fold-body">
          <div class="source-block" data-source-block="89">
            <h3>5  机载 App 远程更新</h3>
          </div>
          <div class="source-block" data-source-block="90">
            <p>本产品通过 Wi-Fi 连接家庭网络后，与八戒云端建立 MQTT 与 HTTP 双向通道。</p>
          </div>
          <div class="source-block" data-source-block="91">
            <p class="source-line">八戒提供目标版本、适用设备、更新说明、更新包 URL 与完整性校验。</p>
          </div>
          <div class="source-block" data-source-block="92">
            <p class="source-line">终端完成版本比对、下载、校验、安装与结果反馈。</p>
          </div>
          <div class="source-block" data-source-block="93">
            <p class="source-line">更新失败不破坏当前可用版本；监测或疗愈进行中默认不安装更新（具体安装时机以 App 内提示为准）。</p>
          </div>
          <div class="source-block" data-source-block="94">
            <p class="source-line">更新过程必须保留未上传成功的睡眠数据。</p>
          </div>
          <div class="source-block" data-source-block="95">
            <p class="source-line">本项仅针对机载App 远程更新；硬件固件版本会上报，但硬件固件远程升级不在 V2.0 默认范围。</p>
          </div>
          <div class="source-block" data-source-block="96">
            <p class="source-line">⚠ App 更新来源必须为八戒官方；切勿点击来路不明的&quot;更新链接&quot;，以免因第三方软件造成触电、信息泄露或设备异常。</p>
          </div>
        </div>
      </details>
      <details class="fold">
        <summary>安全说明与注意事项<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></summary>
        <div class="fold-body">
          <div class="source-block" data-source-block="97">
            <h3>6  安全说明与注意事项</h3>
          </div>
          <div class="source-block" data-source-block="98">
            <p>本章涵盖 GB 4943.1-2022 中常见安全防护要点：电击、火灾、机械与热灼伤、毫米波及 5 GHz 无线局域网辐射。有害物质与废弃处理见第 9 章。</p>
          </div>
          <div class="source-block" data-source-block="99">
            <h3>6.1  电击防护（依据 GB 4943.1-2022 第 5 章、第 6 章）</h3>
          </div>
          <div class="source-block" data-source-block="100">
            <p class="source-line">⚠ 请使用随机配送的电源适配器（DC 5 V / 2 A）。禁止使用任何其它电压/电流规格的适配器、移动电源快充、非原装数据线。</p>
          </div>
          <div class="source-block" data-source-block="101">
            <p class="source-line">禁止用湿手插拔电源；禁止在通电状态下拆卸内部加热组件。</p>
          </div>
          <div class="source-block" data-source-block="102">
            <p class="source-line">禁止在水源、花洒、浴缸、游泳池附近使用本产品。</p>
          </div>
          <div class="source-block" data-source-block="103">
            <p class="source-line">禁止电源线、适配器、本机外壳出现破损后继续使用；如发现破损、过热、异味、冒烟，请立即切断电源并联系售后。</p>
          </div>
          <div class="source-block" data-source-block="104">
            <p class="source-line">清洁维护前请先断开电源。</p>
          </div>
          <div class="source-block" data-source-block="105">
            <p class="source-line">整机跌落、进水或遭受撞击后，应停止使用并联系售后；非原厂授权人员请勿拆机。</p>
          </div>
          <div class="source-block" data-source-block="106">
            <h3>6.2  火灾防护（依据 GB 4943.1-2022 第 5 章、第 6 章）</h3>
          </div>
          <div class="source-block" data-source-block="107">
            <p class="source-line">请保持设备干燥；远离火源、热源、易燃物，避免阳光直射。</p>
          </div>
          <div class="source-block" data-source-block="108">
            <p class="source-line">禁止在主机上覆盖毛巾、衣物、纸张等任何物品；通电时禁止堵塞机身散热孔。</p>
          </div>
          <div class="source-block" data-source-block="109">
            <p class="source-line">禁止主机长时间满载运行（如连续数日不间断使用）。</p>
          </div>
          <div class="source-block" data-source-block="110">
            <p class="source-line">禁止加热模组在精油瓶缺失、空瓶或破损状态下使用。</p>
          </div>
          <div class="source-block" data-source-block="111">
            <h3>6.3  机械与热灼伤防护</h3>
          </div>
          <div class="source-block" data-source-block="112">
            <p class="source-line">整机外壳在正常使用条件下不应有锐利尖端、粗糙毛刺或可能划伤皮肤的部位；如发现上述缺陷，请停止使用并联系售后。</p>
          </div>
          <div class="source-block" data-source-block="113">
            <p class="source-line">开盖后精油加热棒温度较高（可达约 60 ℃），在断电 30 分钟内勿用手直接触碰加热棒金属部分。</p>
          </div>
          <div class="source-block" data-source-block="114">
            <p class="source-line">精油瓶为易碎玻璃，禁止跌落、挤压；如出现漏液请立即拔下电源并用干布擦拭，避免腐蚀底盘。</p>
          </div>
          <div class="source-block" data-source-block="115">
            <p class="source-line">通风孔和扬声器出声孔不可用尖锐物体探入；禁止给儿童把玩或塞入异物。</p>
          </div>
          <div class="source-block" data-source-block="116">
            <h3>6.4  光辐射与微波辐射防护（依据 GB 4943.1-2022 第 7 章、第 10 章）</h3>
          </div>
          <div class="source-block" data-source-block="117">
            <p class="source-line">60 GHz 毫米波雷达为微功率短距离无线电设备；其发射功率远低于国际非电离辐射防护（ICNIRP）公众暴露参考限值，在 0.3 m – 1.5 m 距离内连续暴露对人体安全。</p>
          </div>
          <div class="source-block" data-source-block="118">
            <p class="source-line">体内植入心脏起搏器、除颤器或其他电子医疗器械的用户，及孕妇、婴幼儿，使用前请咨询医生。</p>
          </div>
          <div class="source-block" data-source-block="119">
            <p class="source-line">舒曼波（低频脉冲）开启时，请勿将精密电子设备（如心电贴片、医疗传感器）放置在主机 30 cm 范围内。</p>
          </div>
          <div class="source-block" data-source-block="120">
            <p class="source-line">⚠ 依据《中华人民共和国无线电管理条例》和相关微功率设备使用规定，禁止在以机场跑道中心点为圆心、半径 5000 米的区域内使用本产品；在射电天文台、气象雷达站、卫星地球站（含测控、测距、接收、导航站）等军民用无线电台（站）的电磁环境保护区域内的使用，应遵守电磁环境保护及相关行业主管部门的规定。</p>
          </div>
          <div class="source-block" data-source-block="121">
            <h3>6.5  特殊人群与儿童（依据 GB 4943.1-2022 第 7.12 条）</h3>
          </div>
          <div class="source-block" data-source-block="122">
            <p>本器具不打算由存在肢体、感官或精神能力缺陷或缺少使用经验和知识的人（包括儿童）使用，除非有负责他们安全的人对他们进行与器具使用有关的监督或指导。应照看好儿童，确保他们不玩耍本器具。</p>
          </div>
          <div class="source-block" data-source-block="123">
            <p class="source-line">本产品不是医疗器械，不用于诊断、治疗、监护或缓解疾病。</p>
          </div>
          <div class="source-block" data-source-block="124">
            <p class="source-line">对精油成分过敏者、哮喘患者、孕妇、婴幼儿及免疫低下人群，使用前请咨询医生，并选择无刺激配方。</p>
          </div>
          <div class="source-block" data-source-block="125">
            <p class="source-line">请将本机、适配器、配件放置于儿童无法触及的位置；精油瓶需放置在儿童无法打开、无法打翻的位置。</p>
          </div>
          <div class="source-block" data-source-block="126">
            <h3>6.6  使用环境</h3>
          </div>
          <div class="source-block" data-source-block="127">
            <p class="source-line">工作温度 0 ℃ – 40 ℃，湿度 10 % – 90 % RH（无凝露）。</p>
          </div>
          <div class="source-block" data-source-block="128">
            <p class="source-line">避免在高温、高湿、粉尘或强电磁干扰环境中使用。</p>
          </div>
          <div class="source-block" data-source-block="129">
            <p class="source-line">避免在气压急剧变化（如登机前后）时使用雷达采集功能。</p>
          </div>
          <div class="source-block" data-source-block="130">
            <p class="source-line">请使用稳定的 5 V / 2 A 适配器与标准交流 220 V 电网；山区、农村或老旧建筑电网不稳时，请加装稳压装置。</p>
          </div>
          <div class="source-block" data-source-block="131">
            <h3>6.7  摆放与用电安全</h3>
          </div>
          <div class="source-block" data-source-block="132">
            <p class="source-line">将主机放置于床头平稳干燥处，雷达面正对人体且无大面积遮挡。</p>
          </div>
          <div class="source-block" data-source-block="133">
            <p class="source-line">禁止与其它高功率电器（微波炉、电热水器等）共用同一插座。</p>
          </div>
          <div class="source-block" data-source-block="134">
            <p class="source-line">电源线应避开尖锐边角、活动家具、踏脚位置。</p>
          </div>
          <div class="source-block" data-source-block="135">
            <p class="source-line">设备长时间不使用时，请从插座上拔下插头断开电源。</p>
          </div>
        </div>
      </details>
      <details class="fold">
        <summary>清洁与维护<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></summary>
        <div class="fold-body">
          <div class="source-block" data-source-block="136">
            <h3>7  清洁与维护、芳香耗材管理</h3>
          </div>
          <div class="source-block" data-source-block="137">
            <h3>7.1  清洁与维护</h3>
          </div>
          <div class="source-block" data-source-block="138">
            <p class="source-line">清洁前请先断开电源。</p>
          </div>
          <div class="source-block" data-source-block="139">
            <p class="source-line">使用柔软干布擦拭机身外壳；禁止使用腐蚀性或研磨性清洁剂（如酒精、稀释剂、抛光膏等）。</p>
          </div>
          <div class="source-block" data-source-block="140">
            <p class="source-line">禁止将设备浸入水中或用水直接冲洗。</p>
          </div>
          <div class="source-block" data-source-block="141">
            <p class="source-line">精油槽内部清洁请使用棉签蘸取少量医用酒精轻拭；清洁后请充分晾干再装入精油瓶。</p>
          </div>
          <div class="source-block" data-source-block="142">
            <p class="source-line">扬声器出声孔、雷达出光面请使用干燥、不掉毛的软毛刷轻拭，避免刮花表面。</p>
          </div>
        </div>
      </details>
      <details class="fold">
        <summary>常见问题与保修<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></summary>
        <div class="fold-body">
          <div class="source-block" data-source-block="143">
            <h3>8  常见问题、故障排除与保修</h3>
          </div>
          <div class="source-block" data-source-block="144">
            <h3>8.1  常见问题与故障排查</h3>
          </div>
          <div class="source-block" data-source-block="145">
            <div class="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th scope="col">现象</th>
                    <th scope="col">排查与处理</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row">雷达监测不准确</th>
                    <td>检查主机摆放位置，确保雷达面正对人体胸腹部、无遮挡，距离 0.3 m – 1.5 m。</td>
                  </tr>
                  <tr>
                    <th scope="row">Wi-Fi 连接失败</th>
                    <td>确认密码正确、信号稳定；切换到 2.4 GHz；必要时重启路由器与主机。</td>
                  </tr>
                  <tr>
                    <th scope="row">&quot;雾化效果异常&quot;</th>
                    <td>可能是雾化片干磨，请检查精油瓶是否安装到位、精油是否充足。</td>
                  </tr>
                  <tr>
                    <th scope="row">呼吸引导无法同步</th>
                    <td>放松身体自然跟随引导信号节奏；如音频异常请检查扬声器出声孔是否堵塞。</td>
                  </tr>
                  <tr>
                    <th scope="row">睡眠报告何时生成</th>
                    <td>次日早晨 08:00 自动生成，可扫描二维码查看报告。</td>
                  </tr>
                  <tr>
                    <th scope="row">App 不响应</th>
                    <td>按电源键 10 秒强制重启；更新至最新版本；如仍异常请联系售后。</td>
                  </tr>
                  <tr>
                    <th scope="row">终端长时间不上传</th>
                    <td>检查网络、电源；App 内查看&quot;上次连接时间&quot;；可手动进入睡眠模式触发上报。</td>
                  </tr>
                  <tr>
                    <th scope="row">控制未生效</th>
                    <td>在 App 内查看&quot;结果待确认&quot;状态；如长期未生效，请重启主机或联系售后。</td>
                  </tr>
                  <tr>
                    <th scope="row">香薰频次调整失败</th>
                    <td>调整范围与步长以硬件能力为准；请按页面提示的合法范围操作。</td>
                  </tr>
                  <tr>
                    <th scope="row">设备运行异响</th>
                    <td>检查精油瓶是否安装到位、精油是否充足；如无法消除请停止使用并联系售后。</td>
                  </tr>
                  <tr>
                    <th scope="row">芳香液中有少量絮状物，正常吗？</th>
                    <td>芳香液中如有少量絮状物，是植物成份自然析出的表现，属于正常现象。</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="source-block" data-source-block="146">
            <h3>8.2  保修与服务</h3>
          </div>
          <div class="source-block" data-source-block="147">
            <p>保修范围：</p>
          </div>
          <div class="source-block" data-source-block="148">
            <p class="source-line">主机及雷达模块：自购买之日起 12 个月。</p>
          </div>
          <div class="source-block" data-source-block="149">
            <p class="source-line">电源适配器、Type-C 数据线：自购买之日起 6 个月。</p>
          </div>
          <div class="source-block" data-source-block="150">
            <p class="source-line">精油瓶等耗材：不在保修范围内。</p>
          </div>
          <div class="source-block" data-source-block="151">
            <p>下列情况不在保修范围内：人为损坏、进水、使用非原厂耗材、自行拆卸、私自改装及不可抗力造成的损坏。</p>
          </div>
          <div class="source-block" data-source-block="152">
            <p>售后服务联系方式：</p>
          </div>
          <div class="source-block" data-source-block="153">
            <p class="source-line">客服热线：详见机内保修卡。</p>
          </div>
          <div class="source-block" data-source-block="154">
            <p class="source-line">官方微信公众号：八戒睡眠。</p>
          </div>
          <div class="source-block" data-source-block="155">
            <p class="source-line">售后地址：浙江省杭州市西湖区文三西路478号华星时代广场A座1308。</p>
          </div>
          <div class="source-block" data-source-block="156">
            <p class="source-line">邮件与在线支持渠道详见官方公众号最新公告。</p>
          </div>
          <div class="source-block" data-source-block="157">
            <p>为享受保修服务，请保留完整购买凭证、保修卡与原包装；如需维修请随附故障描述与联系方式。</p>
          </div>
        </div>
      </details>
      <details class="fold">
        <summary>环保与无线电声明<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></summary>
        <div class="fold-body">
          <div class="source-block" data-source-block="158">
            <h3>9  有害物质声明、废弃处理与无线电发射设备声明</h3>
          </div>
          <div class="source-block" data-source-block="159">
            <h3>9.1  有害物质声明</h3>
          </div>
          <div class="source-block" data-source-block="160">
            <p>本产品符合电子电气产品有害物质限制使用的要求。有害物质名称及含量信息按部件分别列于机身贴纸及八戒健康 App 内&quot;合规&quot;。本产品已通过 CCC 强制性产品认证。</p>
          </div>
          <div class="source-block" data-source-block="161">
            <p class="source-line">⚠ 包装与印刷材料中可能含有小零件；请避免 3 岁以下儿童误吞，并按当地垃圾分类要求处理包装材料。</p>
          </div>
          <div class="source-block" data-source-block="162">
            <h3>9.2  废弃处理</h3>
          </div>
          <div class="source-block" data-source-block="163">
            <p>本产品含有电子元器件、电池（如有）、金属与塑料部件，废弃时请勿与生活垃圾混合投放。请按照当地电子产品回收法规，将废弃产品送至指定回收点或联系售后进行回收处理，以保护环境和人体健康。</p>
          </div>
          <div class="source-block" data-source-block="164">
            <p class="source-line">精油瓶残余液体请勿直接倒入下水道或环境；请按当地危险化学品与废液规定处理。</p>
          </div>
          <div class="source-block" data-source-block="165">
            <p class="source-line">电池（如有）请单独投放至专用电池回收桶。</p>
          </div>
          <div class="source-block" data-source-block="166">
            <p class="source-line">包装纸箱、塑料袋、缓冲泡沫等建议分类回收。</p>
          </div>
          <div class="source-block" data-source-block="167">
            <h3>9.3  微功率短距离无线电发射设备声明</h3>
          </div>
          <div class="source-block" data-source-block="168">
            <p>Ⅰ  符合《微功率短距离无线电发射设备目录和技术要求》的具体条款和使用场景，采用的天线类型和性能、控制、调整及开关等使用方法；</p>
          </div>
          <div class="source-block" data-source-block="169">
            <p>Ⅱ  承诺不擅自改变使用场景或使用条件、扩大发射频率范围、加大发射功率（包括额外加装射频功率放大器）、不擅自更改发射天线；</p>
          </div>
          <div class="source-block" data-source-block="170">
            <p>Ⅲ  承诺不对其他合法的无线电台（站）产生有害干扰，也不提出免受有害干扰保护；</p>
          </div>
          <div class="source-block" data-source-block="171">
            <p>Ⅳ  承诺可承受辐射射频能量的工业、科学及医疗（ISM）应用设备的干扰或其他合法的无线电台（站）干扰；</p>
          </div>
          <div class="source-block" data-source-block="172">
            <p>Ⅴ  承诺如对其他合法的无线电台（站）产生有害干扰时，将立即停止使用，并采取措施消除干扰后才继续使用；</p>
          </div>
          <div class="source-block" data-source-block="173">
            <p>Ⅵ  在航空器内和依据法律法规、国家有关规定、标准划设的射电天文台、气象雷达站、卫星地球站（含测控、测距、接收、导航站）等军民用无线电台（站）、机场等的电磁环境保护区域内使用微功率设备，应遵守电磁环境保护及相关行业主管部门的规定；</p>
          </div>
          <div class="source-block" data-source-block="174">
            <p>Ⅶ  禁止在以机场跑道中心点为圆心、半径 5000 米的区域内使用本产品。</p>
          </div>
          <div class="source-block" data-source-block="175">
            <p>本设备使用环境温度范围为 0 ℃ 至 40 ℃，稳定电压为 5 V / 2 A。</p>
          </div>
          <div class="source-block" data-source-block="176">
            <p>本产品同时含 2.4 / 5.1 / 5.8 GHz 无线局域网模块（CMIIT ID 26J333WV5273），该模块不属&quot;微功率&quot;目录范围；其合规信息依 SRRC 2026-23401，详见第 10 章末&quot;制造商信息与执行标准&quot;表。</p>
          </div>
        </div>
      </details>
      <details class="fold">
        <summary>制造商信息与执行标准<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"></path></svg></summary>
        <div class="fold-body">
          <div class="source-block" data-source-block="177">
            <h3>10  制造商信息与执行标准</h3>
          </div>
          <div class="source-block" data-source-block="178">
            <div class="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th scope="col">项目</th>
                    <th scope="col">内容</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row">产品名称</th>
                    <td>睡眠管家</td>
                  </tr>
                  <tr>
                    <th scope="row">软件版本</th>
                    <td>V2.1</td>
                  </tr>
                  <tr>
                    <th scope="row">CMIIT ID</th>
                    <td>26J333WV5273</td>
                  </tr>
                  <tr>
                    <th scope="row">执行安全标准</th>
                    <td>GB 4943.1-2022《音视频、信息技术和通信技术设备 第1部分：安全要求》</td>
                  </tr>
                  <tr>
                    <th scope="row">无线电型号核准</th>
                    <td>60 GHz 毫米波人体感知雷达（微功率短距离无线电设备）；2.4 / 5.1 / 5.8 GHz 无线局域网（CMIIT ID 26J333WV5273）</td>
                  </tr>
                  <tr>
                    <th scope="row">强制性认证</th>
                    <td>已通过 CCC 强制性产品认证（详见机身贴纸）</td>
                  </tr>
                  <tr>
                    <th scope="row">制造商</th>
                    <td>杭州八戒睡眠医学技术有限公司</td>
                  </tr>
                  <tr>
                    <th scope="row">制造商地址</th>
                    <td>中国（浙江）自由贸易试验区杭州市滨江区长河街道江二路57号2幢D区302室</td>
                  </tr>
                  <tr>
                    <th scope="row">客服热线</th>
                    <td>详见机内保修卡</td>
                  </tr>
                  <tr>
                    <th scope="row">官方信息渠道</th>
                    <td>官方微信公众号&quot;八戒睡眠&quot;、八戒健康 App 内公告</td>
                  </tr>
                  <tr>
                    <th scope="row">本说明书版本</th>
                    <td>V2.1（2026 年 10 月）</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="source-block" data-source-block="179">
            <p>—  本说明书完  —</p>
          </div>
        </div>
      </details>
      <router-link class="return-home" :to="manualLink('home')">返回说明首页</router-link>
    </section>
    <section class="view article-view" v-show="topic === 'not-found'">
      <h1 tabindex="-1">这个页面没有找到</h1>
      <p class="lead">请返回目录，重新选择需要的说明。</p>
      <router-link class="inline-link" :to="manualLink('home')">返回说明首页</router-link>
    </section>
  </div>
</template>

<script>
import SleepManualImage from './SleepManualImage.vue'
import { sleepManualLocation } from '@/utils/sleep-manual'

export default {
  name: 'SleepManualContent',
  components: { SleepManualImage },
  props: { topic: { type: String, required: true } },
  methods: {
    manualLink (topic) {
      return sleepManualLocation(topic, this.$route.query)
    }
  }
}
</script>
